'use client';

import { Feature, Permission } from '@prisma/client';
import { Home, Plus } from 'lucide-react';
import { useMemo } from 'react';
import Button from '~/components/Button';
import FeaturePageHeader from '~/components/FeaturePageHeader';
import FeaturePageShell, { featurePageContentClassName } from '~/components/FeaturePageShell';
import { useModal } from '~/components/Modal';
import { SettingsEmptyState } from '~/components/settings/SettingsEmptyState';
import { useViewerBootstrap } from '~/contexts/ViewerBootstrapContext';
import useAbility from '~/hooks/useAbility';
import usePageTitle from '~/hooks/usePageTitle';
import { cn } from '~/lib/utils';
import { api } from '~/utils/api';
import { serverError } from '~/utils/serverError';
import { initNewScanJob } from '../../content-scanner/_components/NewJob';
import NewJobButton from '../../content-scanner/_components/ScanSettings/NewJobButton';
import HomeCharts from './HomeCharts';
import HomeDashboardSkeleton from './HomeDashboardSkeleton';
import HomeWorkTabs from './HomeTabbedLists';

export default function HomeDashboard() {
  usePageTitle('Overview');

  const ability = useAbility();
  const bootstrappedAuthorized = useViewerBootstrap();
  const { data: authorizedQuery } = api.user.authorize.useQuery(undefined, {
    enabled: !bootstrappedAuthorized,
    onError: serverError,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
  const authorized = bootstrappedAuthorized ?? authorizedQuery;
  const enabledFeatures = authorized?.user.tenant.enabledFeatures;

  const canViewReviewQueue =
    !!enabledFeatures?.includes(Feature.MARKETING_TASKS) &&
    !!ability?.can('do', Permission.VIEW_MARKETING_TASK);

  const canViewContentScanner =
    !!enabledFeatures?.includes(Feature.CONTENT_SCANNER) &&
    !!ability?.can('do', Permission.CONTENT_SCANNER);

  const canCreateScanJob =
    canViewContentScanner && !!ability?.can('do', Permission.EDIT_SCANNER_JOB);

  const isGatesLoading = !authorized || !ability;

  const {
    openModal: newJobOpenModal,
    modalRef: newJobModalRef,
    closeModal: newJobCloseModal,
    isOpen: isJobModalOpen,
  } = useModal();

  const initialInput = useMemo(() => initNewScanJob({ isInfluencer: false }), []);

  const hasAnySection = canViewReviewQueue || canViewContentScanner;

  if (isGatesLoading) {
    return <HomeDashboardSkeleton />;
  }

  return (
    <FeaturePageShell>
      <FeaturePageHeader
        title="Overview"
        actions={
          canCreateScanJob ? (
            <Button
              size="sm"
              label="New scan job"
              icon={<Plus className="size-3.5" strokeWidth={2} />}
              onClick={newJobOpenModal}
            />
          ) : undefined
        }
      />

      <div className={cn(featurePageContentClassName, 'space-y-8 py-6')}>
        {canCreateScanJob && (
          <NewJobButton
            hideButton
            initialInput={initialInput}
            isInfluencer={false}
            modalProperties={{
              openModal: newJobOpenModal,
              modalRef: newJobModalRef,
              closeModal: newJobCloseModal,
              isOpen: isJobModalOpen,
            }}
          />
        )}

        {!hasAnySection ? (
          <SettingsEmptyState
            size="page"
            icon={Home}
            title="Welcome To Your Workspace"
            description="Ask an admin to enable Review Queues or Content Scanner so work shows up here."
          />
        ) : (
          <>
            <HomeCharts
              canViewReviewQueue={canViewReviewQueue}
              canViewContentScanner={canViewContentScanner}
            />

            <HomeWorkTabs
              canViewReviewQueue={canViewReviewQueue}
              canViewContentScanner={canViewContentScanner}
              canCreateJob={canCreateScanJob}
              onNewJob={newJobOpenModal}
            />
          </>
        )}
      </div>
    </FeaturePageShell>
  );
}
